import { globalGetService, globalPostService } from '../../../utils/globalApiServices';
import axiosInstance from '../../../utils/apiInterceptor';
import { checkApiStatus } from '../../../utils';

// Creates the upload session for a data room folder; the response carries the session (upload_id)
export const createUploadSessionApi = ({ assetType, assetSlug, folderUuid, totalFiles, totalBytes }) =>
  globalPostService(`records/v4/${assetType}/${assetSlug}/data-room/uploads/`, {
    folder_uuid: folderUuid,
    total_files: totalFiles,
    total_bytes: totalBytes,
  });

// Normalises the session list ({ count, page, limit, results }) into the { list, pagination }
// shape the shared Pagination component expects
export const getUploadSessionsApi = ({ page = 1, per_page = 20 } = {}) =>
  globalGetService('records/v4/data-room/uploads/', { page, limit: per_page })
    .then(response => {
      if (!checkApiStatus(response)) {
        return { ok: false, message: response && response.data ? response.data.message : '', list: [], pagination: null };
      }
      const data = response.data.data || {};
      return {
        ok: true,
        list: data.results || [],
        pagination: { page: data.page || page, per_page: data.limit || per_page, total: data.count || 0 },
      };
    });

// The engine needs failures to reject (the global*Service helpers never settle on network errors),
// so these call the shared axios instance directly and keep its auth header/interceptors
const uploadFilesUrl = (assetType, assetSlug, uploadId) => `records/v4/${assetType}/${assetSlug}/data-room/uploads/${uploadId}/files/`;

// Failed responses keep their data on the error, e.g. data.missing_parts from the complete call
const unwrap = (response) => {
  if (!checkApiStatus(response)) {
    const error = new Error((response && response.data && response.data.message) || 'Request failed');
    error.data = response && response.data ? response.data.data : null;
    throw error;
  }
  return response.data.data || {};
};

export const UPLOAD_TYPE = { SINGLE: 1, MULTIPART: 2 };

// rejected / skipped entries may be plain strings or objects with a reason
const describe = (entry, idKeys) => {
  if (typeof entry === 'string') return { id: entry, reason: null };
  const key = idKeys.find(k => entry && entry[k]);
  return { id: key ? entry[key] : null, reason: entry ? entry.reason || entry.error || entry.message || null : null };
};

/**
 * POST .../uploads/{uploadId}/files/  { files: [{ name, size, type, path }] }
 * Resolves { files: [{ fileId, path, name, status }], rejected: [{ id, reason }] }
 * where a rejected id is the file's path (or name).
 */
export const registerUploadFilesApi = ({ assetType, assetSlug, uploadId, files }) =>
  axiosInstance.post(uploadFilesUrl(assetType, assetSlug, uploadId), { files })
    .then(unwrap)
    .then(data => ({
      files: (data.files || []).map(file => ({ fileId: file.file_id, path: file.path, name: file.name, status: file.status })),
      rejected: (data.rejected || []).map(entry => describe(entry, ['path', 'name'])),
    }));

/**
 * POST .../uploads/{uploadId}/files/urls/  { fileIds: [...] }
 * Resolves { urls: { [fileId]: info }, skipped: [{ id, reason }] } where info is either
 *   SINGLE (under 100 MB):  { uploadType: 1, url, method, headers, expiresAt }
 *     The URL signs content-type, so the returned headers must be sent unchanged on the PUT.
 *   MULTIPART (100 MB+):    { uploadType: 2, partSize, totalParts, partsDone: [n, ...] }
 *     Part URLs come from getUploadPartUrlsApi.
 */
export const getUploadFileUrlsApi = ({ assetType, assetSlug, uploadId, fileIds }) =>
  axiosInstance.post(`${uploadFilesUrl(assetType, assetSlug, uploadId)}urls/`, { fileIds })
    .then(unwrap)
    .then(data => {
      const urls = {};
      (data.urls || []).forEach(item => {
        if (!item.file_id) return;
        if (Number(item.upload_type) === UPLOAD_TYPE.MULTIPART) {
          urls[item.file_id] = {
            uploadType: UPLOAD_TYPE.MULTIPART,
            partSize: Number(item.part_size),
            totalParts: Number(item.total_parts),
            partsDone: (item.parts_done || []).map(Number),
          };
        } else if (item.signed_url) {
          urls[item.file_id] = {
            uploadType: UPLOAD_TYPE.SINGLE,
            url: item.signed_url,
            method: item.method || 'PUT',
            headers: item.headers || null,
            expiresAt: item.expires_at ? Date.parse(item.expires_at) : null,
          };
        }
      });
      return { urls, skipped: (data.skipped || []).map(entry => describe(entry, ['file_id', 'fileId', 'id'])) };
    });

/**
 * POST .../uploads/{uploadId}/files/{fileId}/parts/  { part_numbers: [...] }
 * Resolves { [partNumber]: { url, expiresAt } }. Parts are PUT with no headers.
 */
export const getUploadPartUrlsApi = ({ assetType, assetSlug, uploadId, fileId, partNumbers }) =>
  axiosInstance.post(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/parts/`, { part_numbers: partNumbers })
    .then(unwrap)
    .then(data => {
      const urls = {};
      (data.urls || []).forEach(item => {
        if (item.part_number && item.signed_url) {
          urls[Number(item.part_number)] = { url: item.signed_url, expiresAt: item.expires_at ? Date.parse(item.expires_at) : null };
        }
      });
      return urls;
    });

const SESSION_FILES_PAGE_SIZE = 500;

/**
 * GET .../uploads/{uploadId}/files/?limit=&cursor=&status=  ->  { count, limit, next_cursor, results }
 * Follows next_cursor through every page and resolves [{ fileId, path, size, status, uploadType, uploadedBytes, updatedAt }].
 * status (e.g. 'UPLOADING') limits the list to files in that state.
 * Rejects if paging stops advancing (e.g. the cursor param is ignored) so callers can fall back.
 */
export const getUploadSessionFilesApi = async ({ assetType, assetSlug, uploadId, status }) => {
  const files = [];
  const seenCursors = new Set();
  let cursor = null;
  do {
    const params = { limit: SESSION_FILES_PAGE_SIZE, ...(status ? { status } : {}), ...(cursor !== null ? { cursor } : {}) };
    const data = await axiosInstance.get(uploadFilesUrl(assetType, assetSlug, uploadId), { params }).then(unwrap);
    (data.results || []).forEach(file => files.push({
      fileId: file.file_id,
      path: file.path,
      size: Number(file.file_size),
      status: file.status,
      uploadType: Number(file.upload_type),
      uploadedBytes: Number(file.uploaded_bytes) || 0,
      updatedAt: file.updated_at || null,
    }));
    // next_cursor is a number (possibly 0), null on the last page
    cursor = data.next_cursor === null || data.next_cursor === undefined ? null : data.next_cursor;
    if (cursor !== null && seenCursors.has(cursor)) throw new Error('Upload file list paging did not advance');
    if (cursor !== null) seenCursors.add(cursor);
    if (data.count !== undefined && files.length > Number(data.count)) throw new Error('Upload file list returned more files than its count');
  } while (cursor !== null);
  return files;
};

/**
 * PUT .../uploads/{uploadId}/files/{fileId}/
 * Tells the server a file's final state after the direct-to-storage upload:
 * { status: 'COMPLETED' } or { status: 'FAILED', error_message }. Safe to send twice.
 * For a multipart file the complete call may fail with data.missing_parts; the error keeps it.
 */
export const updateUploadFileStatusApi = ({ assetType, assetSlug, uploadId, fileId, status, uploadedBytes, errorMessage }) =>
  axiosInstance.put(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/`, {
    status,
    ...(uploadedBytes !== undefined ? { uploaded_bytes: uploadedBytes } : {}),
    ...(errorMessage ? { error_message: errorMessage } : {}),
  }).then(unwrap);

/**
 * PUT .../uploads/{uploadId}/files/{fileId}/  { restart: true, file_size }
 * Discards what was uploaded for the file (including parts_done) so a changed file starts over.
 */
export const restartUploadFileApi = ({ assetType, assetSlug, uploadId, fileId, fileSize }) =>
  axiosInstance.put(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/`, { restart: true, file_size: fileSize })
    .then(unwrap);

/**
 * PUT .../uploads/{uploadId}/  { status: 'COMPLETED' }
 * Closes a session that won't complete on its own (some files failed or were never uploaded).
 * The server refuses while any file is still PENDING or UPLOADING, so mark those FAILED first.
 */
export const completeUploadSessionApi = ({ assetType, assetSlug, uploadId }) =>
  axiosInstance.put(`records/v4/${assetType}/${assetSlug}/data-room/uploads/${uploadId}/`, { status: 'COMPLETED' })
    .then(unwrap);

/**
 * PUT .../uploads/{uploadId}/files/{fileId}/  { uploaded_bytes }
 * The upload heartbeat: sent every 30 s for each file being uploaded. It refreshes the file's
 * updated_at, which other tabs, devices and users read to tell a running upload from an interrupted one.
 */
export const reportUploadFileProgressApi = ({ assetType, assetSlug, uploadId, fileId, uploadedBytes }) =>
  axiosInstance.put(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/`, { uploaded_bytes: uploadedBytes })
    .then(unwrap);

// The checker runs several queries per file, so big selections are split to stay under the proxy
// timeout. Each file is checked on its own and nothing is written, so batching is safe.
const NAME_CHECK_SINGLE_REQUEST_MAX = 2000;
const NAME_CHECK_BATCH_SIZE = 500;

/**
 * POST records/v2/file-name-checker/{folderUuid}/  { files: [{ name, path }] }
 * The data room's existing pre-upload check. Resolves
 *   { overwrite_files: [{ name, path }], improper_files: [{ name, path }], file_naming_conventions: [string] }
 * overwrite_files already exist in the folder; improper_files don't follow its naming convention.
 * path is relative to the selected folder, the same path the register call gets. Files in folders
 * that don't exist yet aren't listed at all: they're new, so nothing is overwritten.
 * Up to 2,000 files go in one request; larger selections in batches of 500, one at a time.
 */
export const checkUploadFileNamesApi = async ({ folderUuid, items }) => {
  const batchSize = items.length > NAME_CHECK_SINGLE_REQUEST_MAX ? NAME_CHECK_BATCH_SIZE : items.length;
  const result = { overwrite_files: [], improper_files: [], file_naming_conventions: [] };
  for (let offset = 0; offset < items.length; offset += batchSize) {
    const files = items.slice(offset, offset + batchSize).map(({ file, relativePath }) => ({ name: file.name, path: relativePath }));
    const data = await axiosInstance.post(`records/v2/file-name-checker/${folderUuid}/`, { files }).then(unwrap);
    result.overwrite_files.push(...(data.overwrite_files || []));
    result.improper_files.push(...(data.improper_files || []));
    // Same list in every response
    if (!result.file_naming_conventions.length) result.file_naming_conventions = data.file_naming_conventions || [];
  }
  return result;
};
